import { useEffect, useState, type FormEvent } from 'react';
import { useOp } from '../api.ts';
import { UndoLastSave, WandButton, WandProposal } from '../companion/WandUi.tsx';
import { useWand, type WandController } from '../companion/useWand.ts';
import { entriesFor, typedFields, wandKey, wandPlaces, type WandEntry, type WandField } from '../companion/wand.ts';
import { Link } from '../router.tsx';
import type { CouponDetail, CouponFactsDto, CouponState, CouponVariant } from '../types.ts';
import { announceChange, runWrite, useChanges } from '../ui/changes.ts';
import { useSiteDraft } from '../ui/site-draft.ts';
import { Drawer, ErrorBox, Loading, Modal, StateBadge, formatDate } from '../ui/kit.tsx';
import { useToast } from '../ui/toasts.tsx';
import { CouponFactsFields, factsFromDto, factsToInput, type FactsDraft } from './coupon-facts.tsx';
import { CouponMoreActions } from './catalog/routes.tsx';

const TRANSITION_LABELS: Record<CouponState, string> = {
  approved: 'Approve',
  rejected: 'Reject',
  paused: 'Pause',
  archived: 'Archive',
  pending_review: 'Back to review',
};
const TRANSITION_DONE: Record<CouponState, string> = {
  approved: 'approved',
  rejected: 'rejected',
  paused: 'paused',
  archived: 'archived',
  pending_review: 'back in review',
};
const NEEDS_REASON: CouponState[] = ['rejected', 'paused'];

export function CouponDrawer({ couponRef, onClose }: { couponRef: string; onClose: () => void }) {
  const changes = useChanges();
  const detail = useOp<CouponDetail>('admin.coupons.get', { ref: couponRef }, changes);
  const data = detail.data;
  const title = data ? `${data.merchant.name}: ${data.coupon.title}` : 'Coupon';
  return (
    <Drawer title={title} onClose={onClose} wide>
      {detail.error ? <ErrorBox error={detail.error} onRetry={detail.reload} /> : null}
      {!data && detail.loading ? <Loading /> : null}
      {data ? <CouponEditor detail={data} /> : null}
    </Drawer>
  );
}

function CouponEditor({ detail }: { detail: CouponDetail }) {
  const { coupon } = detail;
  const toast = useToast();
  const wand = useWand(coupon.ref, toast);
  // Sparse: the wand only appears where a proposal makes sense and the person may edit the field.
  const places = wandPlaces(detail);
  const editableSites = places.sites;
  const conditionsWand = places.conditions;
  const wholeCoupon = () => {
    const fields: WandField[] = [...(editableSites.length ? (['title', 'description'] as const) : []), ...(conditionsWand ? (['conditions'] as const) : [])];
    void wand.ask('all', { ...(editableSites.length ? { siteRefs: editableSites } : {}), fields });
  };
  return (
    <div className="coupon-editor">
      <div className="coupon-editor-head">
        <p className="muted small">
          <Link to={`/merchants/${detail.merchant.ref}`}>{detail.merchant.name}</Link> · {coupon.kind === 'code' ? 'Code' : 'Deal'} · {coupon.discount} · source {coupon.source.type}
          {coupon.source.confidence !== null ? ` (${Math.round(coupon.source.confidence * 100)}% confidence)` : ''}
          {coupon.source.url ? (
            <>
              {' · '}
              <a href={coupon.source.url} target="_blank" rel="noreferrer noopener">
                evidence
              </a>
            </>
          ) : null}{' '}
          · added by {coupon.createdBy} {formatDate(coupon.createdAt)}
        </p>
        {editableSites.length || conditionsWand ? <WandButton label="propose the whole coupon (every field you can edit, per site)" status={wand.status} busy={wand.busy('all')} onClick={wholeCoupon} /> : null}
        {wand.lastSave ? <UndoLastSave label={wand.lastSave.label} undoUntil={wand.lastSave.undoUntil} onUndo={() => void wand.undo()} /> : null}
      </div>
      {detail.flags.length ? (
        <ul className="flags" aria-label="Open QA flags">
          {detail.flags.map((flag, index) => (
            <li key={index} className={`flag flag-${flag.severity}`}>
              <strong>{flag.flag}</strong> {flag.message}
            </li>
          ))}
        </ul>
      ) : null}
      <h3 className="section-title">On each site</h3>
      <div className="variant-grid" style={{ gridTemplateColumns: `repeat(${Math.max(1, detail.variants.length)}, minmax(220px, 1fr))` }}>
        {detail.variants.map((variant) => (
          <VariantColumn key={variant.propertyRef} coupon={coupon} variant={variant} wand={wand} />
        ))}
      </div>
      <FactsEditor coupon={coupon} canEdit={detail.canEditFacts} sharing={detail.sharing} wand={wand} conditionsWand={conditionsWand} />
      {detail.canEditFacts ? <CouponMoreActions couponRef={coupon.ref} /> : null}
      <details className="history">
        <summary>History ({detail.history.length})</summary>
        <ol>
          {detail.history.map((entry, index) => (
            <li key={index}>
              <time dateTime={entry.at}>{new Date(entry.at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</time> {entry.action.replace(/_/g, ' ')} by {entry.actor}
              {entry.detail ? <span className="muted"> · {entry.detail}</span> : null}
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
}

function sharingLabel(sharing: CouponDetail['sharing']): string {
  const sites = `${sharing.sites} site${sharing.sites === 1 ? '' : 's'}`;
  const orgs = `${sharing.organizations} organization${sharing.organizations === 1 ? '' : 's'}`;
  return `Shared across ${sites} in ${orgs}`;
}

function FactsEditor({ coupon, canEdit, sharing, wand, conditionsWand }: { coupon: CouponFactsDto; canEdit: boolean; sharing: CouponDetail['sharing']; wand: WandController; conditionsWand: boolean }) {
  const toast = useToast();
  const server = factsFromDto(coupon);
  const form = useSiteDraft<FactsDraft>(server);
  const entry = wand.entries[wandKey(null, 'conditions')];
  useEffect(() => wand.reportTyped(null, typedFields({ conditions: form.draft.conditions }, { conditions: server.conditions }, entry ? [entry] : [])));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await form.save(async (draft) => {
      // An accepted wand proposal: the conditions go through its changeset (audited, undoable), the rest as usual.
      const viaWand = await wand.saveThroughWand(null, 'Conditions', draft.conditions !== server.conditions ? { conditions: draft.conditions } : {});
      if (viaWand === 'failed') return;
      const others = JSON.stringify(factsToInput({ ...draft, conditions: '' })) !== JSON.stringify(factsToInput({ ...server, conditions: '' }));
      if (viaWand === 'saved' && !others) {
        toast('success', 'Saved: conditions');
        announceChange();
        return;
      }
      await runWrite<{ changed: string[] }>(toast, 'admin.coupons.updateFacts', { ref: coupon.ref, patch: factsToInput(draft) }, (r) =>
        r.changed.length || viaWand === 'saved' ? `Saved: ${[...(viaWand === 'saved' ? ['conditions'] : []), ...r.changed].join(', ')}` : 'Nothing changed',
      );
    });
  };
  const acceptConditions = (accepted: WandEntry) => {
    if (accepted.proposed === null) return;
    form.setDraft({ ...form.draft, conditions: accepted.proposed });
    wand.accept(accepted.key);
  };
  return (
    <form className="facts-editor" onSubmit={submit} aria-labelledby="facts-heading">
      <h3 className="section-title" id="facts-heading">
        Shared facts <small className="muted" data-testid="sharing">{sharingLabel(sharing)}</small>
      </h3>
      <p className="muted small">
        Code, discount, conditions and dates are the same everywhere. Title and description here are only the starting text for sites the coupon is offered to later; each site edits its own text above.
        {!canEdit && sharing.outsideYourAccess > 0 ? ` ${sharing.outsideYourAccess} of these sites are outside your access, so only a catalog owner can change the facts.` : ''}
      </p>
      <CouponFactsFields
        draft={form.draft}
        onChange={form.setDraft}
        disabled={!canEdit}
        extras={
          canEdit && conditionsWand
            ? {
                conditions: {
                  action: <WandButton label="propose clearer conditions (every site showing it)" status={wand.status} busy={wand.busy(wandKey(null, 'conditions'))} onClick={() => void wand.ask(wandKey(null, 'conditions'), { fields: ['conditions'] })} />,
                  below: entry ? <WandProposal entry={entry} siteName="the facts" onAccept={() => acceptConditions(entry)} onReject={() => wand.reject(entry.key)} onRetry={(note) => void wand.retry(entry, note)} /> : null,
                },
              }
            : {}
        }
      />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={!canEdit || form.saving || !form.dirty}>
          {form.saving ? 'Saving…' : `Save facts (${sharingLabel(sharing).toLowerCase()})`}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={!form.dirty}
          onClick={() => {
            form.reset();
            if (entry) wand.reject(entry.key);
          }}
        >
          Reset
        </button>
      </div>
    </form>
  );
}

interface VariantDraft {
  title: string;
  description: string;
  priority: string;
  editorsPick: boolean;
  hidden: boolean;
}

/** The server's current values for one site: its own title and description as values, never placeholders. */
const variantDraft = (v: CouponVariant): VariantDraft => ({
  title: v.title,
  description: v.description,
  priority: String(v.priority),
  editorsPick: v.editorsPick,
  hidden: v.hidden,
});

/**
 * One site's column. Its draft, dirty state and save button are its own: saving another site reloads the data,
 * but this column keeps its unsaved edits (and says so) instead of being reset.
 */
function VariantColumn({ coupon, variant, wand }: { coupon: CouponFactsDto; variant: CouponVariant; wand: WandController }) {
  const toast = useToast();
  const { draft, setDraft, dirty, saving, save: saveDraft, reset } = useSiteDraft(variantDraft(variant));
  const [reasonFor, setReasonFor] = useState<CouponState | null>(null);
  const id = `v-${variant.propertyRef}`;
  const site = variant.propertyRef;
  const wandEntries = entriesFor(wand.entries, site);
  const entryOf = (field: WandField) => wand.entries[wandKey(site, field)];
  const showWand = variant.offered && variant.canEdit;
  useEffect(() => wand.reportTyped(site, typedFields({ title: draft.title, description: draft.description }, { title: variant.title, description: variant.description }, wandEntries)));

  const save = () =>
    saveDraft(async (d) => {
      const server = variantDraft(variant);
      const textChanged = d.title !== server.title || d.description !== server.description;
      // An accepted wand proposal: the site text is saved as its changeset (audited, undoable as one change).
      const viaWand = await wand.saveThroughWand(site, variant.propertyName, textChanged ? { title: d.title, description: d.description } : {});
      if (viaWand === 'failed') return;
      const settings = d.priority !== server.priority || d.editorsPick !== server.editorsPick || d.hidden !== server.hidden;
      if (viaWand === 'saved' && !settings) {
        toast('success', `${variant.propertyName}: saved`);
        announceChange();
        return;
      }
      await runWrite(
        toast,
        'admin.coupons.updateVariant',
        {
          couponRef: coupon.ref,
          propertyRef: variant.propertyRef,
          ...(viaWand === 'saved' ? {} : { titleOverride: d.title, descriptionOverride: d.description }),
          priority: Number.parseInt(d.priority, 10) || 0,
          editorsPick: d.editorsPick,
          hidden: d.hidden,
        },
        `${variant.propertyName}: saved`,
      );
    });

  const accept = (entry: WandEntry) => {
    if (entry.proposed === null || (entry.field !== 'title' && entry.field !== 'description')) return;
    setDraft({ ...draft, [entry.field]: entry.proposed });
    wand.accept(entry.key);
  };
  const wandFor = (field: 'title' | 'description') =>
    showWand ? <WandButton label={`propose a ${field} for ${variant.propertyName}`} status={wand.status} busy={wand.busy(wandKey(site, field))} onClick={() => void wand.ask(wandKey(site, field), { siteRefs: [site], fields: [field] })} /> : null;
  const proposalFor = (field: 'title' | 'description') => {
    const entry = entryOf(field);
    return entry ? <WandProposal entry={entry} siteName={variant.propertyName} onAccept={() => accept(entry)} onReject={() => wand.reject(entry.key)} onRetry={(note) => void wand.retry(entry, note)} /> : null;
  };

  const transition = (to: CouponState, reason?: string) =>
    runWrite(toast, 'admin.coupons.transition', { couponRef: coupon.ref, propertyRef: variant.propertyRef, to, reason }, `${variant.propertyName}: ${TRANSITION_DONE[to]}`);

  const offer = () => runWrite(toast, 'admin.coupons.offerToSites', { couponRef: coupon.ref, propertyRefs: [variant.propertyRef] }, `Offered to ${variant.propertyName}; it is now in review`);

  return (
    <section className={`variant ${variant.live ? 'variant-live' : ''}`} aria-labelledby={`${id}-title`} data-site={variant.propertyName}>
      <header>
        <h4 id={`${id}-title`}>{variant.propertyName}</h4>
        <small className="muted">{variant.brandName}</small>
        <StateBadge state={variant.state} live={variant.live} />
        {dirty ? (
          <span className="unsaved" role="status" data-testid="unsaved">
            Unsaved
          </span>
        ) : null}
      </header>
      {variant.offered ? (
        <>
          {variant.live ? (
            <p className="live-note">Live on this site</p>
          ) : (
            <div className="why-not">
              <strong>Why isn’t this live?</strong>
              <ul>
                {variant.notLive.map((reason) => (
                  <li key={reason.code}>{reason.message}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="field-head">
            <label htmlFor={`${id}-title-input`}>Title on this site</label>
            {wandFor('title')}
          </div>
          <input id={`${id}-title-input`} value={draft.title} disabled={!variant.canEdit || saving} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          {proposalFor('title')}
          <div className="field-head">
            <label htmlFor={`${id}-desc`}>Description on this site</label>
            {wandFor('description')}
          </div>
          <textarea id={`${id}-desc`} rows={2} value={draft.description} disabled={!variant.canEdit || saving} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          {proposalFor('description')}
          <div className="variant-row">
            <label>
              Priority
              <input type="number" className="input-narrow" value={draft.priority} disabled={!variant.canEdit} onChange={(e) => setDraft({ ...draft, priority: e.target.value })} />
            </label>
            <label className="inline-check">
              <input type="checkbox" checked={draft.editorsPick} disabled={!variant.canEdit} onChange={(e) => setDraft({ ...draft, editorsPick: e.target.checked })} />
              Editor’s pick
            </label>
            <label className="inline-check">
              <input type="checkbox" checked={draft.hidden} disabled={!variant.canEdit} onChange={(e) => setDraft({ ...draft, hidden: e.target.checked })} />
              Hidden
            </label>
          </div>
          <div className="variant-actions">
            <button type="button" className="btn btn-small btn-primary" disabled={!dirty || !variant.canEdit || saving} onClick={() => void save()}>
              {saving ? 'Saving…' : dirty ? `Save ${variant.propertyName}` : `${variant.propertyName}: up to date`}
            </button>
            {dirty ? (
              <button
                type="button"
                className="btn btn-small btn-ghost"
                onClick={() => {
                  reset();
                  for (const entry of wandEntries) wand.reject(entry.key);
                }}
              >
                Discard
              </button>
            ) : null}
            {variant.transitions.map((to) => (
              <button
                key={to}
                type="button"
                className={`btn btn-small ${to === 'approved' ? 'btn-ok' : to === 'rejected' || to === 'archived' ? 'btn-danger-ghost' : ''}`}
                onClick={() => (NEEDS_REASON.includes(to) ? setReasonFor(to) : transition(to))}
              >
                {variant.state === 'paused' && to === 'approved' ? 'Resume' : TRANSITION_LABELS[to]}
              </button>
            ))}
          </div>
          {variant.pauseReason ? <p className="muted small">Paused: {variant.pauseReason}</p> : null}
        </>
      ) : (
        <div className="not-offered">
          <p className="muted">Not offered to this site.</p>
          {variant.canEdit ? (
            <button type="button" className="btn btn-small" onClick={offer}>
              Offer to {variant.propertyName}
            </button>
          ) : null}
        </div>
      )}
      {reasonFor ? <ReasonDialog action={TRANSITION_LABELS[reasonFor]} siteName={variant.propertyName} onCancel={() => setReasonFor(null)} onConfirm={(reason) => { setReasonFor(null); void transition(reasonFor, reason); }} /> : null}
    </section>
  );
}

function ReasonDialog({ action, siteName, onCancel, onConfirm }: { action: string; siteName: string; onCancel: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      title={`${action} on ${siteName}`}
      onClose={onCancel}
      actions={
        <>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={reason.trim().length < 3} onClick={() => onConfirm(reason.trim())}>
            {action}
          </button>
        </>
      }
    >
      <label htmlFor="reason-input">Reason (kept in the history)</label>
      <input id="reason-input" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && reason.trim().length >= 3 && onConfirm(reason.trim())} />
    </Modal>
  );
}
